/**
 * SSR entry bundle for the Inchcape.React.AspNet pipeline.
 *
 * Loaded once per V8 engine at warm-up time. Imports every macro's expose.tsx
 * for its side effect (each file assigns `global.<ComponentName> = Component`),
 * then defines the named entrypoints C# calls into:
 *
 *   global.__ssrRender(name, propsJson) -> string  (HTML)
 *   global.__ssrRenderRouter(name, propsJson, url) -> string (JSON of {html, redirect?})
 *
 * Returns empty string for components not registered on the global scope, which
 * lets the C# helper fall through cleanly to client-only render.
 */

import React from 'react';
import ReactDOM from 'react-dom';
import ReactDOMServer from 'react-dom/server';
import { StaticRouter } from 'react-router-dom';
import { ClientFooter } from '../../legacy/js/components/HeaderFooter/ClientFooter';

declare const global: any;

global.React = React;
global.ReactDOM = ReactDOM;
global.ReactDOMServer = ReactDOMServer;
global.ClientFooter = ClientFooter;

// Browser-only macros — custom elements that touch HTMLElement / customElements / window
// and render CLIENT-side via their own bundle (not via @Html.React SSR). They MUST be
// excluded from the SSR bundle: executing them in the server V8 engine (no DOM) throws
// e.g. `ReferenceError: HTMLElement is not defined`, which poisons the SSR engine so every
// server-rendered page 500s (prod incident 2026-07-06 — the <cdp-recs> element). Add any
// such client-only macro's directory name here. (cdp-recs is also import-safe via its lazy
// defineCdpRecsElement, but it has no SSR representation, so it should never load here.)
const SSR_EXCLUDED_MACROS = ['cdp-recs'];

// Auto-import every (SSR-able) macro's expose.tsx for its side-effect registration.
// webpack's require.context evaluates each required file, so each `global.<MacroName> =
// Component` assignment runs at engine warm-up — except the excluded client-only macros.
const macrosContext = (require as any).context(
  '../../macros',
  true,
  /\/expose\.tsx$/
);
macrosContext
  .keys()
  .filter(
    (key: string) =>
      !SSR_EXCLUDED_MACROS.some((name) => key.includes(`/${name}/`))
  )
  .forEach((key: string) => macrosContext(key));

global.__ssrRender = function (name: string, propsJson: string): string {
  const Component = global[name];
  if (!Component) {
    return '';
  }
  const props = propsJson ? JSON.parse(propsJson) : {};
  return ReactDOMServer.renderToString(React.createElement(Component, props));
};

global.__ssrRenderRouter = function (
  name: string,
  propsJson: string,
  url: string
): string {
  const Component = global[name];
  if (!Component) {
    return JSON.stringify({ html: '' });
  }
  const props = propsJson ? JSON.parse(propsJson) : {};
  const context: { url?: string } = {};
  const html = ReactDOMServer.renderToString(
    React.createElement(
      StaticRouter,
      { location: url, context },
      React.createElement(Component, props)
    )
  );
  return context.url
    ? JSON.stringify({ html: '', redirect: context.url })
    : JSON.stringify({ html });
};
